import { createElement as rc, useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { uiRuntimeConfig, filters, metadata } from 'lib_ui-services';
import useDbView from '../../hooks/useDbView';
import { View, styled, useDebounce, fromTheme, webOnlyStyles } from 'lib_ui-primitives';
import logging from '@sstdev/lib_logging';
import useNetwork from '../../hooks/useNetwork';
import useGetDbViewName from '../../hooks/useDbView/useGetDbViewName';
import BaseIcon from 'lib_ui-primitives/src/components/Icons/BaseIcon.gl2025';

const { filterHelpers } = filters;
const pageSize = uiRuntimeConfig.getPageSizeForLimitSyncSizeRelations();

const KeySearchContainer = styled(View)`
    align-items: center;
    justify-content: center;
    flex-direction: row;
`;

let SearchWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: ${fromTheme('keySearch', 'backgroundColor')};
    border-radius: ${fromTheme('keySearch', 'borderRadius')};
    border-width: 1px;
    border-color: ${fromTheme('keySearch', 'borderColor')};
    padding: ${fromTheme('keySearch', 'padding')};
    gap: ${fromTheme('keySearch', 'gap')};
    min-width: 200px;
`;

SearchWrapper = webOnlyStyles(SearchWrapper)`
    border-style: solid;
    transition: border-color 0.2s ease;

    &:focus-within {
        border-color: ${fromTheme('colorScheme', 'primary')};
    }
`;

const SearchIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${fromTheme('keySearch', 'iconColor')};
`;

const SearchInput = styled.input`
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    min-width: 0;
    line-height: ${fromTheme('keySearch', 'input', 'lineHeight')};
    font-family: ${fromTheme('font')};
    font-size: ${fromTheme('fontSize')};
    color: ${fromTheme('keySearch', 'input', 'color')};
`;

const _p = {
    useDbView,
    useGetDbViewName,
    getRelationMetadata: metadata.getRelationMetadata
};
export const _private = _p;

function KeySearchGl2025(props) {
    const {
        searchText,
        hNode,
        hNode: {
            id,
            namespace,
            relation
        }
    } = props ?? { hNode: { treePosition: {} } };

    const { isConnected } = useNetwork();
    const [inputValue, setInputValue] = useState(searchText || '');
    const searchInputRef = useRef();
    const viewName = _p.useGetDbViewName(namespace, relation);
    const { viewCriteria } = _p.useDbView(namespace, relation, viewName, hNode);
    let defaultFilters = useMemo(() => filterHelpers.getDefaultFilters(hNode), [hNode]);

    const { supportsFullTextSearch } = _p.getRelationMetadata(namespace, relation);

    if (!defaultFilters?.propertiesToSearch) {
        __PRODUCTION__ &&
            logging.error(
                'Missing `propertiesToSearch` filter on `KeySearch`. You might have to move it up from the grid in blockly.'
            );
        defaultFilters = {
            propertiesToSearch: { propertiesToSearch: ['assetNo', 'title', 'description'] },
            ...defaultFilters
        };
    }

    const updateSearchOptions = useDebounce(
        function (updatedSearchText) {
            if (updatedSearchText.trim() === '') {
                viewCriteria.removeFilters(viewName, 'fullTextSearch');
                viewCriteria.applyFilters(defaultFilters, viewName);
            } else {
                viewCriteria.applyFilters(
                    {
                        paged: { page: 0, offset: 0, limit: supportsFullTextSearch ? pageSize : undefined },
                        ...defaultFilters,
                        fullTextSearch: {
                            searchTerm: updatedSearchText.trim()
                        }
                    },
                    viewName
                );
            }
        },
        [viewCriteria, defaultFilters, viewName, supportsFullTextSearch],
        uiRuntimeConfig.getSearchDebounceMilliseconds() || 250,
        {
            leading: false,
            trailing: true
        }
    );

    useEffect(() => {
        return () => {
            viewCriteria.removeFilters(viewName, 'fullTextSearch');
        };
    }, [viewCriteria, viewName]);

    const debouncedSearch = useDebounce(
        newValue => {
            updateSearchOptions(newValue);
        },
        [updateSearchOptions],
        uiRuntimeConfig.getSearchDebounceMilliseconds() || 250,
        {
            leading: false,
            trailing: true
        }
    );

    function handleInputChange(e) {
        const newValue = e.target.value;
        setInputValue(newValue);  // Update UI immediately
        debouncedSearch(newValue); // Debounce the search
    }

    const placeholder = supportsFullTextSearch && isConnected
        ? 'Enter 3 characters...'
        : 'Search...';

    return rc(
        KeySearchContainer,
        {
            id,
            'data-testid': 'keysearch-container'
        },
        rc(
            SearchWrapper,
            null,
            rc(
                SearchIcon,
                null,
                rc(BaseIcon, {
                    name: 'search',
                    size: 'md',
                    color: 'currentColor'
                })
            ),
            rc(SearchInput, {
                id: id + '_input',
                type: 'text',
                value: inputValue,
                onChange: handleInputChange,
                placeholder,
                maxLength: 100,
                ref: searchInputRef,
                'data-testid': 'keysearch-input',
                'aria-label': placeholder
            })
        )
    );
}

KeySearchGl2025.propTypes = {
    dispatch: PropTypes.func,
    title: PropTypes.string,
    searchText: PropTypes.string,
    window: PropTypes.object,
    forAction: PropTypes.string,
    currentRoute: PropTypes.string,
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired
};

export default KeySearchGl2025;
